import { ArrowLeft } from "lucide-react";
import { EMAIL, WA_DISPLAY, waLink, trackEvent } from "@/lib/vgt";
import { Seo } from "@/components/Seo";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "1. Who We Are",
        p: "Vanilla Global Transindo (VGT) is an Indonesian vanilla beans supplier and exporter serving international business-to-business (B2B) buyers, including food manufacturers, bakeries, gelato and ice cream makers, chocolate manufacturers, importers, and distributors. For any privacy question, contact us at team@vanillaglobaltransindo.com or on WhatsApp at +62 816 1567 4745.",
      },
      {
        h: "2. Information We Collect",
        p: "When you submit a quotation request on this website, we collect the business contact details you provide: your full name, company or business name, business email address, phone or WhatsApp number, destination country, buyer category, product interest, estimated quantity, and any notes you choose to add. We also record standard technical data such as your IP address at the time of submission, used solely for spam prevention and security.",
      },
      {
        h: "3. How We Use Your Information",
        p: "We use the information you submit for one purpose: to respond to your inquiry with pricing, product specifications, and shipping options, and to follow up on your request by email or WhatsApp. We do not use your details for unrelated marketing, and we do not send unsolicited bulk email.",
      },
      {
        h: "4. How We Store and Protect It",
        p: "Your inquiry is stored in our secured database with access restricted to authorized VGT team members, and is delivered to our team by email through our email service provider. We apply reasonable technical and organizational measures to protect your information against unauthorized access, loss, or misuse.",
      },
      {
        h: "5. Sharing With Third Parties",
        p: "We do not sell, rent, or trade your information. Your data is processed only by the service providers that operate our website hosting, database, and email delivery, strictly to provide those services to us. We may disclose information if required to do so by applicable Indonesian law or a valid legal process.",
      },
      {
        h: "6. International Processing",
        p: "Because we serve buyers worldwide, your information may be processed on servers located outside your country. By submitting a quotation request, you acknowledge this transfer, which is necessary to respond to your inquiry.",
      },
      {
        h: "7. Analytics & Advertising Measurement",
        p: "This website may use privacy-respecting analytics and, when enabled by us, Google Ads conversion measurement to understand how visitors reach and use this page. These tools measure events such as form submissions and button clicks; no personally identifiable information is shared with advertisers.",
      },
      {
        h: "8. Data Retention",
        p: "We retain inquiry records for as long as needed to manage our business relationship with you and to meet legal or accounting obligations. You may request earlier deletion at any time.",
      },
      {
        h: "9. Your Rights",
        p: "You may request access to, correction of, or deletion of the information you submitted, at any time and free of charge. Email team@vanillaglobaltransindo.com and we will action your request promptly.",
      },
      {
        h: "10. Changes to This Policy",
        p: "We may update this Privacy Policy from time to time. The latest version is always published on this page with its update date.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "1. Scope",
        p: "These Terms & Conditions apply to all inquiries, quotations, and business-to-business transactions with Vanilla Global Transindo (VGT), an Indonesian vanilla beans supplier and exporter. By submitting a quotation request, you confirm you are acting on behalf of a business.",
      },
      {
        h: "2. Quotations and Orders",
        p: "Submitting a quotation request through this website or WhatsApp does not create a binding order or contract. Prices, product availability, and shipping options are confirmed in writing per inquiry. A transaction is only binding once both parties confirm the order details in writing (email or WhatsApp), including product, quantity, price, and payment terms.",
      },
      {
        h: "3. Pricing and Shipping Costs",
        p: "All quoted prices are for the products only. Shipping costs are not included in product prices and are quoted separately per destination, so you always see the full landed cost before confirming an order.",
      },
      {
        h: "4. Minimum Order Quantity",
        p: "Our minimum order quantity starts from 1 kg. We accommodate evaluation-scale orders for growing producers as well as full commercial volumes for industrial buyers.",
      },
      {
        h: "5. Product Specifications",
        p: "Specifications such as moisture content, vanillin level, and bean length are stated per batch on this website and in our quotations. Because vanilla is a natural agricultural product, minor variation between batches is normal; batch-specific specifications are confirmed before order confirmation.",
      },
      {
        h: "6. Samples",
        p: "Samples are available on request so buyers can verify quality before a commercial order. Sample and shipping costs depend on the product and destination, and are quoted per inquiry.",
      },
      {
        h: "7. Payment",
        p: "Payment terms are confirmed together with your quotation. We accept international bank transfer (T/T). Orders are packed and dispatched after payment confirmation as agreed in writing.",
      },
      {
        h: "8. Export Documentation and Customs",
        p: "Where applicable, shipments are supported with a Phytosanitary Certificate, Certificate of Analysis, Commercial Invoice, and Packing List. Import duties, taxes, and customs clearance in the destination country are the buyer's responsibility unless otherwise agreed in writing.",
      },
      {
        h: "9. Delivery and Risk",
        p: "We arrange express air freight and assist with shipping coordination. Delivery timeframes are estimates provided in good faith and may be affected by carriers, weather, or customs processes beyond our control.",
      },
      {
        h: "10. Liability",
        p: "To the maximum extent permitted by law, VGT's liability for any claim related to an order is limited to the value of the goods in that order. We are not liable for indirect or consequential losses.",
      },
      {
        h: "11. Website Content",
        p: "All content on this website, including text and images, belongs to VGT and may not be reproduced without permission. Information on this page is provided in good faith and may be updated without notice.",
      },
      {
        h: "12. Contact",
        p: "For any questions about these Terms & Conditions, contact us at team@vanillaglobaltransindo.com or WhatsApp +62 816 1567 4745.",
      },
    ],
  },
};

export default function Legal({ page }) {
  const doc = CONTENT[page] || CONTENT.privacy;
  return (
    <div data-testid={`legal-page-${page}`} className="min-h-screen bg-[#FDFBF7] text-[#2C1A0C] font-sans">
      <Seo
        title={`${doc.title} — Vanilla Global Transindo`}
        description={`${doc.title} of Vanilla Global Transindo (VGT), Indonesian vanilla beans supplier and exporter for international B2B buyers.`}
        path={`/${page}`}
      />
      <header className="border-b border-[#E6DEC3] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <a href="/indonesian-vanilla-supplier" data-testid="legal-logo-link" className="flex items-center gap-3">
            <img
              src="/images/vgt-logo.jpg"
              alt="Vanilla Global Transindo logo"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-[#E6DEC3]"
            />
            <span className="font-serif font-bold text-[#2C1A0C]">Vanilla Global Transindo</span>
          </a>
          <a
            href="/indonesian-vanilla-supplier"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E4F3E] hover:text-[#1B3B2B] transition-colors"
          >
            <ArrowLeft size={15} /> Back to site
          </a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <h1 data-testid="legal-title" className="font-serif font-bold text-3xl sm:text-4xl tracking-tight">
          {doc.title}
        </h1>
        <p className="mt-3 font-mono text-xs tracking-[0.2em] uppercase text-[#6E5B4F]">{doc.updated}</p>
        <div className="mt-10 space-y-8">
          {doc.sections.map((s, i) => (
            <section key={s.h} data-testid={`legal-section-${i}`}>
              <h2 className="font-serif font-bold text-xl text-[#2C1A0C]">{s.h}</h2>
              <p className="mt-2.5 text-sm sm:text-base text-[#6E5B4F] leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>
        <div className="mt-14 border-t border-[#E6DEC3] pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-[#6E5B4F]">
            Questions? {EMAIL} · WhatsApp {WA_DISPLAY}
          </p>
          <a
            data-testid="legal-whatsapp-link"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: `legal_${page}` })}
            className="shrink-0 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EAE52] text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors"
          >
            Chat on WhatsApp
          </a>
        </div>
      </main>
    </div>
  );
}
